/**
 * Local-first mutations for TUTORING (M3).
 *
 * They write to PowerSync → write-path (`db/sync.rs::submission_version`),
 * which applies the REAL authz:
 *   • new version: ACTIVELY enrolled student on their own submission (respects
 *     `allow_resubmission`/`max_versions`; the previous one must be RETURNED);
 *   • review (feedback+outcome): cohort reviewer (tutor/lead/assistant/
 *     admin); `feedback_by` is SEALED by the server.
 *
 * SLA/resubmission config is WRITTEN via REST (not here): see the REST client
 * `features/academy/tutor/api.ts`.
 */

import { and, desc, eq, type PowerSyncDrizzleDB, submission_version } from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateId } from "@fleet/utils";
import { patchRowVerified, type VerifiedWriteOptions } from "../../powersync/verified-write";

/** Highest existing version of a submission (0 if there isn't one yet). */
async function currentMaxVersion(
  db: PowerSyncDrizzleDB,
  assignmentId: string,
  cohortId: string,
  userId: string,
): Promise<{ version: number; outcome: string | null } | null> {
  const rows = await db
    .select({ version: submission_version.version, outcome: submission_version.outcome })
    .from(submission_version)
    .where(
      and(
        eq(submission_version.assignment_id, assignmentId),
        eq(submission_version.cohort_id, cohortId),
        eq(submission_version.user_id, userId),
      ),
    )
    .orderBy(desc(submission_version.version))
    .limit(1);
  const r = rows[0];
  return r ? { version: r.version ?? 0, outcome: r.outcome ?? null } : null;
}

/**
 * The STUDENT creates a NEW version of their submission (resubmission). The
 * server recomputes the authoritative version number and seals `late`; here
 * it's optimistic only. Returns the id of the created version.
 */
export async function submitNewVersionMutation(
  db: PowerSyncDrizzleDB,
  params: {
    assignmentId: string;
    cohortId: string;
    organizationId: string;
    userId: string;
    body?: string | null;
    files?: string[];
    groupId?: string | null;
  },
): Promise<string> {
  const now = nowISO();
  const latest = await currentMaxVersion(db, params.assignmentId, params.cohortId, params.userId);
  const version = (latest?.version ?? 0) + 1;
  const id = generateId();
  await db.insert(submission_version).values({
    id,
    assignment_id: params.assignmentId,
    cohort_id: params.cohortId,
    organization_id: params.organizationId,
    user_id: params.userId,
    group_id: params.groupId ?? null,
    version,
    body: params.body ?? null,
    files: JSON.stringify(params.files ?? []),
    submitted_at: now,
    outcome: null,
    created_at: now,
    updated_at: now,
  });
  return id;
}

/**
 * Edits the pending DRAFT (same version) before the tutor reviews it. The server only
 * allows it if it's still pending (outcome IS NULL) and belongs to the actor.
 * ⚠️ Dead code: no callers in `apps/web`.
 */
export async function updatePendingVersionMutation(
  db: PowerSyncDrizzleDB,
  versionId: string,
  updates: { body?: string | null; files?: string[] },
): Promise<void> {
  const patch: Record<string, unknown> = { submitted_at: nowISO(), updated_at: nowISO() };
  if (updates.body !== undefined) {
    patch.body = updates.body;
  }
  if (updates.files !== undefined) {
    patch.files = JSON.stringify(updates.files);
  }
  await db.update(submission_version).set(patch).where(eq(submission_version.id, versionId));
}

export type ReviewOutcome = "returned" | "approved" | "rejected";

/**
 * ⚠️ No `scope` on purpose, even though `submission_version` has
 * `organization_id`/`cohort_id`. 2026-08-13 audit: the real boundary is
 * "reviewer OF THE COHORT" (cohort-scoped, not an organization capability),
 * and the caller (`tutor-panel.tsx`) is an aggregated review QUEUE that
 * pools submissions from SEVERAL cohorts at once (`submission_version`
 * travels via `my_cohorts_teacher`/`org_admin_scope` — the header comment
 * itself says so). There's no single "active org/cohort" for that screen:
 * the only cohort value available is `v().cohort_id`, which is a field OF
 * THE VERY ROW being written — using it as `scope` would be circular (the
 * row would be verified against data read from itself), zero real
 * protection, same case as `markNotificationReadMutation`. Truly closing it
 * would require checking `v.cohort_id` against the SET of cohorts the
 * reviewer has authorized — which is exactly what the sync filter already
 * does, not something a single `scope` parameter can express.
 * Signal that would reopen it: a review landing on a submission from a
 * cohort outside `my_cohorts_teacher`/`org_admin_scope`.
 *
 * The REVIEWER seals feedback + outcome on a pending version. `feedback_by`
 * is forced by the server; here it's optimistic with the actor's own userId
 * to reflect in the UI without waiting for the sync-back.
 */
export async function reviewVersionMutation(
  db: PowerSyncDrizzleDB,
  params: {
    versionId: string;
    reviewerUserId: string;
    outcome: ReviewOutcome;
    feedback?: string | null;
  },
  options?: VerifiedWriteOptions,
): Promise<void> {
  const now = nowISO();
  await patchRowVerified(
    db,
    submission_version,
    submission_version.id,
    params.versionId,
    {
      feedback: params.feedback ?? null,
      feedback_by: params.reviewerUserId,
      feedback_at: now,
      outcome: params.outcome,
    },
    options,
  );
}
